import { queryOptions, useQuery } from '@tanstack/react-query'

import {
  clientDistrictFilterService,
  DistrictFilterService,
  type DistrictsResponse,
  type DistrictCity,
  type DistrictNeighborhoodVicinity,
  type DistrictRequestParams,
} from './district-filter.service'

import axios, { type AxiosRequestHeaders } from 'axios'

import { localeToCountryCode, type Locales } from '@/utils/language'
import { ApiHeaders } from '@/shared/libs/context-enhancer'
import { createSolarApiClient } from '@/shared/libs/api-client'

import type { QueryClient } from '@tanstack/react-query'

// Re-export types for backward compatibility
export type { DistrictsResponse, DistrictCity, DistrictNeighborhoodVicinity, DistrictRequestParams }

/* API */
export const getAllDistricts = async (headers: ApiHeaders): Promise<DistrictsResponse> => {
  const districtFilterService = new DistrictFilterService(
    createSolarApiClient({ headers: headers as unknown as AxiosRequestHeaders }),
  )
  return districtFilterService.getAllDistricts(localeToCountryCode(headers['Accept-Language']))
}

/* CLIENT SIDE */
export const useDistrictReader = (
  headers: ApiHeaders,
  locale: Locales,
): {
  data: DistrictsResponse | undefined
  isLoading: boolean
  error: unknown
} => {
  return useQuery(getDistrictsQueryOptions(headers))
}

/* SERVER SIDE */
export const prefetchDistricts = async (
  queryClient: QueryClient,
  headers: ApiHeaders,
): Promise<DistrictsResponse | undefined> => {
  const locale = headers['Accept-Language']
  await queryClient.prefetchQuery(getDistrictsQueryOptions(headers))
  return queryClient.getQueryData(getDistrictsQueryKey(locale))
}

/* SHARED */
export const getDistrictsQueryOptions = (headers: ApiHeaders) => {
  const locale = headers['Accept-Language']
  return queryOptions({
    queryKey: getDistrictsQueryKey(locale),
    queryFn: () => {
      return getAllDistricts(headers)
    },
    staleTime: 60 * 60 * 1000,
  })
}

export const getDistrictsQueryKey = (locale: Locales) => {
  return ['districts', locale]
}

/* UTIL */
export class DistrictResponseController {
  private data: DistrictsResponse | undefined

  constructor(data: DistrictsResponse | undefined) {
    this.data = data
  }

  getCodeAndNameMap(): Record<string, string> | undefined {
    return this.data?.cityList.reduce(
      (acc, city) => {
        acc[city.code] = city.name

        for (const vicinity of city.neighborhoodVicinityList) {
          acc[vicinity.id] = vicinity.name
        }
        return acc
      },
      {} as Record<string, string>,
    )
  }

  getCodeAndNameList(): [string, string][] {
    return Object.entries(this.getCodeAndNameMap() || {})
  }

  getCityByCode(code: string): DistrictCity | undefined {
    return this.data?.cityList.find((city) => city.code === code)
  }

  getVicinityByCode(code: string): DistrictNeighborhoodVicinity | undefined {
    return this.data?.cityList.flatMap((city) => city.neighborhoodVicinityList).find((vicinity) => vicinity.id === code)
  }

  getByCode(code: string): { code: string; name: string } | undefined {
    const vicinity = this.getVicinityByCode(code)
    if (vicinity) {
      return {
        code: vicinity.id,
        name: vicinity.name,
      }
    }
    const city = this.getCityByCode(code)
    if (city) {
      return {
        code: city.code,
        name: city.name,
      }
    }
    return undefined
  }

  getTargetDistrictsByCode(code: string): { code: string; name: string }[] {
    const targetCity = this.getCityByCode(code)
    if (targetCity) {
      return targetCity.neighborhoodVicinityList.map((vicinity) => ({
        code: vicinity.id,
        name: vicinity.name,
      }))
    }

    const targetVicinity = this.getVicinityByCode(code)
    if (targetVicinity) {
      return [
        {
          code: targetVicinity.id,
          name: targetVicinity.name,
        },
      ]
    }

    return []
  }
}
